import React, { useCallback, useState } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";
import { FormControl, FormItem, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { ImageIcon, Cross2Icon, ReloadIcon } from "@radix-ui/react-icons";
import { MoveUp, MoveDown } from "lucide-react";

const UPLOAD_FILE = gql`
  mutation UPLOAD_FILE($file: Upload!) {
    uploadFile(file: $file)
  }
`;

interface ImageUploadProps {
  field: ControllerRenderProps<any, any>;
  maxImages?: number;
  enableReordering?: boolean;
  variant?: "default" | "large";
}

export function ImageUpload({
  field,
  maxImages = 3,
  enableReordering = false,
  variant = "default",
}: ImageUploadProps) {
  const [uploadFile] = useMutation(UPLOAD_FILE);
  const [isUploading, setIsUploading] = useState(false);
  const images = Array.isArray(field?.value) ? field.value : [];

  const handleImageUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (images.length >= maxImages) {
        console.warn("Maximum number of images reached");
        return;
      }

      setIsUploading(true);
      try {
        const { data } = await uploadFile({
          variables: { file },
        });

        if (data?.uploadFile) {
          const newImages = [...images, data.uploadFile];
          field.onChange(newImages);
        }
      } catch (error) {
        console.error("Upload error:", error);
      } finally {
        setIsUploading(false);
      }
    },
    [images, field.onChange, field.name, uploadFile, maxImages]
  );

  const handleRemoveImage = (imageUrl: string) => {
    const newImages = images.filter((url: string) => url !== imageUrl);
    field.onChange(newImages);
  };

  const handleMoveImage = (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === images.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const newImages = [...images];
    const temp = newImages[index];
    newImages[index] = newImages[newIndex];
    newImages[newIndex] = temp;

    field.onChange(newImages);
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (!file || !file.type.startsWith("image/")) return;

      if (images.length >= maxImages) {
        console.warn("Maximum number of images reached");
        return;
      }

      setIsUploading(true);
      try {
        const { data } = await uploadFile({
          variables: { file },
        });

        if (data?.uploadFile) {
          const newImages = [...images, data.uploadFile];
          field.onChange(newImages);
        }
      } catch (error) {
        console.error("Upload error:", error);
      } finally {
        setIsUploading(false);
      }
    },
    [images, field.onChange, field.name, uploadFile, maxImages]
  );

  // ${
  //   images.length < maxImages
  //     ? "p-4 border-2 border-dashed rounded-lg transition-colors " +
  //       (isDragging
  //         ? "border-primary bg-primary/5"
  //         : "border-gray-200")
  //     : ""
  // }

  return (
    <div
      className={` col-span-2 w-full   ${
        images.length < maxImages
          ? "p-4 border-2 border-dashed rounded-lg transition-colors " +
            (isDragging ? "border-primary bg-primary/5" : "border-gray-200")
          : ""
      }`}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Image Preview */}
      <div className={variant === "large" ? "w-full" : "flex flex-wrap gap-4"}>
        {images?.map((imageUrl: string, index: number) => (
          <div
            key={index}
            className={`relative group border rounded-md overflow-hidden ${
              variant === "large" ? "w-full" : ""
            }`}
          >
            <img
              src={imageUrl}
              alt={`Upload ${index + 1}`}
              className={
                variant === "large"
                  ? "w-full h-48 object-cover"
                  : "w-24 h-24 object-cover"
              }
            />
            <div className="absolute top-1 right-1 flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 bg-white/80 hover:bg-white/90"
                onClick={() => handleRemoveImage(imageUrl)}
              >
                <Cross2Icon className="h-4 w-4" />
              </Button>

              {enableReordering && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 bg-white/80 hover:bg-white/90"
                    onClick={() => handleMoveImage(index, "up")}
                    disabled={index === 0}
                  >
                    <MoveUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 bg-white/80 hover:bg-white/90"
                    onClick={() => handleMoveImage(index, "down")}
                    disabled={index === images.length - 1}
                  >
                    <MoveDown className="h-4 w-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {/* Upload Button - Only shown if below maxImages limit */}
      {images.length < maxImages && (
        <div className="flex items-center justify-between gap-4 w-full">
          <p className="text-sm text-gray-500">Drag and drop an image here</p>
          <input
            type="file"
            id={`image-upload-${field.name}`}
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              document.getElementById(`image-upload-${field.name}`)?.click()
            }
          >
            {isUploading ? (
              <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ImageIcon className="mr-2 h-4 w-4" />
            )}
            {isUploading ? "Uploading..." : "Upload Image"}
          </Button>
        </div>
      )}
    </div>
  );
}
